import React, { Suspense, useEffect, useState } from 'react';
import { Outlet, createRootRoute } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '../lib/query-client';
import { ToastProvider, useToast } from '../components/ui/Toast';
import { clearSession } from '../lib/session';
import { AUTH_LOGOUT_EVENT, AUTH_FORBIDDEN_EVENT } from '../lib/query-client';
import { ThemeProvider } from '../components/ui/ThemeProvider';
import { NotFound } from '../components/error/NotFound';
import { InternalError } from '../components/error/InternalError';
import { Forbidden } from '../components/error/Forbidden';
import { OfflineProvider } from '../components/error/OfflineProvider';
import { ApiError } from '@repo/api-client';
import { reportWebError } from '../lib/error-reporter';
import { BrandingProvider } from '../components/branding/BrandingProvider';
import { PageLoader } from '../components/ui/PageLoader';
import { CookieConsent } from '../components/ui/CookieConsent';

const CommandPalette = React.lazy(() =>
  import('../components/ui/CommandPalette').then((m) => ({ default: m.CommandPalette }))
);

function RootInner() {
  const { toast } = useToast();
  const [commandOpen, setCommandOpen] = useState(false);

  useEffect(() => {
    const logoutHandler = () => {
      clearSession();
      queryClient.clear();
      window.location.href = '/login';
    };
    window.addEventListener(AUTH_LOGOUT_EVENT, logoutHandler);
    return () => window.removeEventListener(AUTH_LOGOUT_EVENT, logoutHandler);
  }, []);

  useEffect(() => {
    const handler = () => {
      toast({
        type: 'error',
        title: 'Access denied',
        description: 'You do not have permission to perform this action.',
      });
    };
    window.addEventListener(AUTH_FORBIDDEN_EVENT, handler);
    return () => window.removeEventListener(AUTH_FORBIDDEN_EVENT, handler);
  }, [toast]);

  const commandItems = [
    { id: 'dashboard', label: 'Go to Dashboard', group: 'Navigate', onSelect: () => { window.location.href = '/'; } },
    { id: 'settings-password', label: 'Change Password', group: 'Settings', onSelect: () => { window.location.href = '/settings/password'; } },
    { id: 'settings-sessions', label: 'Active Sessions', group: 'Settings', onSelect: () => { window.location.href = '/settings/sessions'; } },
  ];

  return (
    <>
      <Outlet />
      <Suspense fallback={null}>
        <CommandPalette
          items={commandItems}
          open={commandOpen}
          onOpenChange={setCommandOpen}
        />
      </Suspense>
    </>
  );
}

export const Route = createRootRoute({
  component: function Root() {
    return (
      <QueryClientProvider client={queryClient}>
        <ThemeProvider defaultTheme="light" storageKey="kraphene-theme">
          <ToastProvider>
            <BrandingProvider>
              <OfflineProvider>
                <RootInner />
                <CookieConsent />
              </OfflineProvider>
            </BrandingProvider>
          </ToastProvider>
        </ThemeProvider>
      </QueryClientProvider>
    );
  },
  notFoundComponent: () => (
    <ThemeProvider defaultTheme="light" storageKey="kraphene-theme">
      <NotFound />
    </ThemeProvider>
  ),
  onCatch: (error) => {
    // Router's CatchBoundary intercepts loader/render errors before they reach window.onerror
    // or the top-level React ErrorBoundary, so without this they never reach Sentry at all.
    reportWebError(error);
  },
  errorComponent: ({ error, reset }) => {
    if (error instanceof ApiError) {
      if (error.status === 404) {
        return (
          <ThemeProvider defaultTheme="light" storageKey="kraphene-theme">
            <NotFound />
          </ThemeProvider>
        );
      }
      if (error.status === 403) {
        return (
          <ThemeProvider defaultTheme="light" storageKey="kraphene-theme">
            <Forbidden />
          </ThemeProvider>
        );
      }
    }
    return (
      <ThemeProvider defaultTheme="light" storageKey="kraphene-theme">
        <InternalError error={error as Error} reset={reset} />
      </ThemeProvider>
    );
  },
});
